import type { LucideIcon } from 'lucide-react';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { Card } from './Card';
import { Skeleton } from './Skeleton';
import { cn } from '@/utils/cn';

type Tone = 'brand' | 'success' | 'warning' | 'danger' | 'neutral';

const ICON_TONES: Record<Tone, string> = {
  brand: 'bg-brand-50 text-brand-600',
  success: 'bg-emerald-50 text-emerald-600',
  warning: 'bg-amber-50 text-amber-600',
  danger: 'bg-red-50 text-red-600',
  neutral: 'bg-canvas text-muted',
};

interface StatCardProps {
  icon: LucideIcon;
  label: string;
  value: string | number;
  hint?: string;
  /** Percentage change against the previous period. Positive is not always good — see `invertDelta`. */
  delta?: number;
  /** For metrics where a rise is bad, such as out-of-stock lines. */
  invertDelta?: boolean;
  tone?: Tone;
  isLoading?: boolean;
}

export function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  delta,
  invertDelta = false,
  tone = 'brand',
  isLoading = false,
}: StatCardProps) {
  const isUp = (delta ?? 0) >= 0;
  const isGood = invertDelta ? !isUp : isUp;
  const DeltaIcon = isUp ? TrendingUp : TrendingDown;

  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-muted truncate text-xs font-medium">{label}</p>

          {isLoading ? (
            <Skeleton className="mt-2 h-7 w-20" />
          ) : (
            <p className="text-ink mt-1.5 truncate text-2xl font-semibold">{value}</p>
          )}

          {hint && !isLoading ? <p className="text-muted mt-1 truncate text-xs">{hint}</p> : null}
        </div>

        <span
          className={cn('grid size-10 shrink-0 place-items-center rounded-lg', ICON_TONES[tone])}
        >
          <Icon className="size-5" aria-hidden />
        </span>
      </div>

      {delta !== undefined && !isLoading ? (
        <p
          className={cn(
            'mt-3 inline-flex items-center gap-1 text-xs font-medium',
            isGood ? 'text-emerald-600' : 'text-red-600',
          )}
        >
          <DeltaIcon className="size-3.5" aria-hidden />
          {/* Numbers stay LTR inside an RTL paragraph so the sign reads correctly. */}
          <span dir="ltr">
            {isUp ? '+' : ''}
            {delta}%
          </span>
        </p>
      ) : null}
    </Card>
  );
}
